/**
 * Artwork slice — Redux Toolkit state + thunks for the customer artwork
 * library, in-flight upload queue, validation polling, and order-item
 * attachment.
 *
 * State shape
 * -----------
 * ```ts
 * {
 *   files: ArtworkFile[],            // persisted library records
 *   uploadQueue: UploadQueueEntry[], // transient client-side upload entries
 *   isLoading: boolean,              // gates the list / delete API calls
 *   error: string | null,            // last slice-level error (normalised string)
 * }
 * ```
 *
 * Thunks (1:1 with `@dfq/shared/api/artwork.ts`)
 * ----------------------------------------------
 *  - {@link fetchArtworkLibrary}     GET    /account/artwork
 *  - {@link deleteArtworkFile}       DELETE /account/artwork/:id
 *  - {@link uploadArtworkFile}       merges a completed multipart upload
 *  - {@link fetchFileValidationStatus} polls /artwork/:id/status
 *  - {@link attachFileToOrderItem}   POST   /orders/:orderId/artwork
 *
 * Polling
 * -------
 * `fetchFileValidationStatus` uses a setTimeout-based async loop (NOT
 * setInterval) so the thunk's `signal.aborted` cancellation tears the
 * loop down cleanly. Minimum 2-second interval between requests; max
 * 20 attempts before the thunk rejects with a timeout error.
 *
 * Error handling
 * --------------
 * Every rejected handler stores `action.error.message ?? 'Unknown error'` —
 * never an `Error` instance. This keeps the slice serializable for
 * Redux DevTools and the persisted store snapshot.
 *
 * Per `.claude/rules/frontend-data-flow.md`:
 *  - Capsules MUST NOT import this slice. Containers do.
 *  - Thunks call `@dfq/shared/api/artwork.ts` helpers — never axios
 *    directly.
 *  - The slice doesn't import from `@dfq/shared/api/artwork`'s
 *    consumers — no circular deps.
 *
 * Pattern reference: `cartSlice` (SF-CART-001) — co-located thunks,
 * typed state interface, builder.addCase pattern, named selector
 * exports memoised with `createSelector`.
 *
 * SH-FILES-003.
 */
import {
  createAsyncThunk,
  createListenerMiddleware,
  createSelector,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';

import * as artworkApi from '@dfq/shared/api/artwork';
import type {
  AttachFileToOrderItemResponse,
  UploadArtworkFileArg,
} from '@dfq/shared/api/artwork';
import { uploadArtworkFile as uploadArtworkFileApi } from '@dfq/shared/api/artworkUploader';
import { generateThumbnail } from '@dfq/shared/lib/imageThumbnail';
import { login, logout } from '@dfq/shared/stores/authSlice';
import {
  clearPendingArtworkFile,
  readPendingArtworkFile,
} from '@dfq/shared/stores/pendingArtworkStore';
import type {
  ArtworkFile,
  SelectedArtwork,
  UploadQueueEntry,
  ValidationStatus,
} from '@dfq/shared/types/artwork';

// ----------------------------------------------------------------------------
// Re-exports for callers that import shared types via the slice path
// (kept stable for SH-FILES-001 consumers).
// ----------------------------------------------------------------------------

export type {
  ArtworkFile,
  ArtworkFileVersion,
  SelectedArtwork,
  ValidationCheck,
  ValidationResult,
  ValidationStatus,
  UploadQueueEntry,
} from '@dfq/shared/types/artwork';

/**
 * Legacy minimal artwork shape — SH-FILES-001's FileUploader runner
 * returns this subset (only the fields the upload-complete step echoes
 * back, not the full library record). The slice's thunks expect the
 * canonical {@link ArtworkFile} type with all spec fields populated;
 * containers that need to feed a multipart-complete result into the
 * slice should hydrate the missing fields (validation results,
 * width/height/dpi etc.) from the subsequent
 * `fetchFileValidationStatus` poll or library fetch.
 *
 * Kept as a distinct type rather than aliased to {@link ArtworkFile}
 * because the FileUploader runner contract shipped with SH-FILES-001
 * is intentionally narrower.
 */
export interface ArtworkFileDTO {
  id: number;
  original_filename: string;
  s3_key: string;
  s3_version_id?: string | null;
  file_size_bytes: number;
  mime_type: string;
  validation_status: string;
  uploaded_at: string;
}

// ----------------------------------------------------------------------------
// Legacy upload-progress shape (kept for FileUploader compatibility)
// ----------------------------------------------------------------------------

/**
 * Lifecycle states for a single FileUploader entry. Shared with the
 * capsule's local reducer — exported so containers and other surfaces
 * can switch on the same union without re-deriving it.
 */
export type ArtworkUploadStatus =
  | 'queued'
  | 'uploading'
  | 'complete'
  | 'error'
  | 'cancelled';

/**
 * Per-upload progress projection synced from FileUploader local state
 * into Redux. Mirrors the shape SH-FILES-001 originally defined; the
 * slice still accepts these via {@link artworkUploadProgress} for
 * backwards compatibility, internally translating to a
 * {@link UploadQueueEntry}.
 */
export interface ArtworkUploadProgress {
  /** Stable client-side id (uuid-ish) generated by the FileUploader. */
  uploadId: string;
  /** Original filename. */
  filename: string;
  /** 0–100. Aggregated from per-chunk `onprogress` events. */
  percent: number;
  /** Lifecycle state. */
  status: ArtworkUploadStatus;
  /** Human-readable error message, present when `status === 'error'`. */
  errorMessage?: string;
  /** Server-confirmed record, present when `status === 'complete'`. */
  artworkFile?: ArtworkFileDTO;
}

// ----------------------------------------------------------------------------
// State
// ----------------------------------------------------------------------------

export interface ArtworkSliceState {
  /** Persisted artwork library records — populated by fetchArtworkLibrary. */
  files: ArtworkFile[];
  /** Transient per-upload progress entries. */
  uploadQueue: UploadQueueEntry[];
  /** Gates list / delete / attach API calls. */
  isLoading: boolean;
  /** Last slice-level error message (normalised to a string). */
  error: string | null;
  /**
   * The artwork the customer wants to use on the next product they
   * configure. See {@link SelectedArtwork} for the full contract.
   *
   * SH-ARTWORK-001.
   */
  selectedArtwork: SelectedArtwork | null;
}

const initialState: ArtworkSliceState = {
  files: [],
  uploadQueue: [],
  isLoading: false,
  error: null,
  selectedArtwork: null,
};

// ----------------------------------------------------------------------------
// Anonymous selection — sessionStorage echo
// ----------------------------------------------------------------------------

const SESSION_STORAGE_KEY = 'dfq_selected_artwork';

/**
 * Subset of {@link SelectedArtwork} that survives the sessionStorage
 * round-trip. The `thumbnailUrl` for an anonymous selection is an
 * `objectURL:` / `data:` URL tied to the in-memory `File`, which is
 * gone after a refresh — so we don't echo it. On rehydrate, an
 * anonymous selection without a recoverable thumbnail is dropped.
 *
 * Persisted selections (with an `artworkFileId`) carry a CDN URL and
 * can be fully echoed.
 */
interface SessionStorageEcho {
  artworkFileId: number | null;
  filename: string;
  selectedAt: string;
  widthPx: number | null;
  heightPx: number | null;
  dpi: number | null;
  autoAttach: boolean;
  /**
   * Only persisted artwork echoes the URL — for anonymous, the blob is
   * gone after a refresh, so we omit it and rehydrate skips the entry.
   */
  thumbnailUrl: string | null;
  /** IndexedDB draft id — present for anonymous deferred-draft selections. */
  draftId?: string | null;
}

function writeSessionEcho(selected: SelectedArtwork | null): void {
  if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') {
    return;
  }
  try {
    if (selected === null) {
      window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
      return;
    }
    const echo: SessionStorageEcho = {
      artworkFileId: selected.artworkFileId,
      filename: selected.filename,
      selectedAt: selected.selectedAt,
      widthPx: selected.widthPx,
      heightPx: selected.heightPx,
      dpi: selected.dpi,
      autoAttach: selected.autoAttach,
      thumbnailUrl: selected.artworkFileId !== null ? selected.thumbnailUrl : null,
      draftId: selected.draftId ?? null,
    };
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(echo));
  } catch {
    /* sessionStorage may throw in private-mode browsers — non-fatal */
  }
}

/**
 * Read the sessionStorage echo, if present. Returns `null` for an
 * anonymous echo (no thumbnail URL means we can't reconstruct the
 * preview, so callers should treat it as "no selection") and for any
 * malformed payload. Persisted echoes are returned with the full
 * thumbnail URL.
 */
export function readSelectedArtworkFromSession(): SelectedArtwork | null {
  if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (raw === null) return null;
    const echo = JSON.parse(raw) as SessionStorageEcho;
    if (echo.thumbnailUrl === null) {
      // Anonymous selection — blob is unrecoverable after refresh.
      window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
      return null;
    }
    return {
      artworkFileId: echo.artworkFileId,
      thumbnailUrl: echo.thumbnailUrl,
      filename: echo.filename,
      widthPx: echo.widthPx,
      heightPx: echo.heightPx,
      dpi: echo.dpi,
      selectedAt: echo.selectedAt,
      autoAttach: echo.autoAttach,
      draftId: echo.draftId ?? null,
    };
  } catch {
    return null;
  }
}

/**
 * Revoke an `objectURL:` thumbnail to release the underlying blob.
 * No-op for `data:` and `https:` URLs.
 */
function revokeIfObjectUrl(url: string | null | undefined): void {
  if (url === null || url === undefined) return;
  if (typeof URL === 'undefined') return;
  if (url.startsWith('blob:')) {
    try {
      URL.revokeObjectURL(url);
    } catch {
      /* no-op */
    }
  }
}

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

/**
 * Translate the FileUploader's legacy {@link ArtworkUploadProgress}
 * shape (camelCase, percent / errorMessage) into the slice's
 * {@link UploadQueueEntry} shape (snake-friendly progress / error).
 *
 * `cancelled` collapses to `error` because the queue's status union
 * doesn't model that lifecycle directly — containers that need to tell
 * cancelled from real failures inspect the `error` field.
 */
function progressToQueueEntry(
  progress: ArtworkUploadProgress,
): UploadQueueEntry {
  let status: UploadQueueEntry['status'];
  switch (progress.status) {
    case 'queued':
      status = 'queued';
      break;
    case 'uploading':
      status = 'uploading';
      break;
    case 'complete':
      status = 'complete';
      break;
    case 'error':
    case 'cancelled':
      status = 'error';
      break;
  }

  const entry: UploadQueueEntry = {
    id: progress.uploadId,
    filename: progress.filename,
    progress: progress.percent,
    status,
  };
  if (progress.errorMessage !== undefined) {
    entry.error = progress.errorMessage;
  }
  if (progress.artworkFile?.id !== undefined) {
    entry.artworkFileId = progress.artworkFile.id;
  }
  return entry;
}

/**
 * Upsert (replace-by-id or append) a queue entry. Used by both
 * {@link artworkUploadProgress} and the upload thunk's fulfilled
 * handler so the same id is never duplicated.
 */
function upsertQueueEntry(
  queue: UploadQueueEntry[],
  next: UploadQueueEntry,
): UploadQueueEntry[] {
  const idx = queue.findIndex((entry) => entry.id === next.id);
  if (idx === -1) {
    queue.push(next);
    return queue;
  }
  queue[idx] = next;
  return queue;
}

// ----------------------------------------------------------------------------
// Thunks
// ----------------------------------------------------------------------------

/**
 * `GET /api/v1/account/artwork` — fetch the authenticated customer's
 * artwork library. On fulfillment replaces the slice's `files` array
 * (does NOT append) so a second dispatch reflects the latest server
 * state rather than duplicating records.
 */
export const fetchArtworkLibrary = createAsyncThunk<ArtworkFile[]>(
  'artwork/fetchArtworkLibrary',
  async () => artworkApi.fetchArtworkLibrary(),
);

/**
 * `DELETE /api/v1/account/artwork/:id` — delete a single artwork file.
 * On fulfillment splices the matching record out of `files` using the
 * id passed as the action argument.
 */
export const deleteArtworkFile = createAsyncThunk<number, number>(
  'artwork/deleteArtworkFile',
  async (id) => {
    await artworkApi.deleteArtworkFile(id);
    return id;
  },
);

/**
 * Merges a completed multipart upload into both `files` (persisted
 * record) and the matching `uploadQueue` entry (status flipped to
 * `complete`). Accepts the multipart-upload result from
 * `artworkUploader.ts`'s {@link uploadArtworkFile} — the actual S3
 * orchestration is owned by that module; this thunk is the
 * "completion event" the slice consumes.
 */
export const uploadArtworkFile = createAsyncThunk<
  { uploadId: string; file: ArtworkFile },
  UploadArtworkFileArg
>('artwork/uploadArtworkFile', async (arg) => {
  return artworkApi.uploadArtworkFile(arg);
});

/**
 * Polling configuration. `POLLING_INTERVAL_MS` is the minimum delay
 * between two consecutive `/artwork/:id/status` requests;
 * `POLLING_MAX_ATTEMPTS` caps the total number of requests before the
 * thunk rejects with a timeout error.
 */
export const POLLING_INTERVAL_MS = 2_000;
export const POLLING_MAX_ATTEMPTS = 20;

/**
 * Polls `GET /api/v1/artwork/:id/status` until the validation status
 * is no longer `pending`, or until {@link POLLING_MAX_ATTEMPTS}
 * attempts have elapsed (≈40 seconds at the 2-second floor). Each
 * poll dispatches an intermediate `setValidationStatusForFile` action
 * so the UI reflects the latest server-side validation state during
 * the poll without waiting for the loop to fulfill.
 *
 * Cancellation: respects `thunkAPI.signal.aborted` between attempts
 * AND forwards the signal to the API client so an in-flight HTTP
 * request is also aborted.
 */
export const fetchFileValidationStatus = createAsyncThunk<
  Pick<ArtworkFile, 'id' | 'validation_status' | 'validation_results_json'>,
  number
>(
  'artwork/fetchFileValidationStatus',
  async (id, thunkAPI) => {
    for (let attempt = 0; attempt < POLLING_MAX_ATTEMPTS; attempt += 1) {
      if (thunkAPI.signal.aborted) {
        throw new Error('Polling cancelled');
      }

      const result = await artworkApi.fetchFileValidationStatus(id, {
        signal: thunkAPI.signal,
      });

      // Push intermediate state into `files` so the UI reflects the
      // latest server-side validation state during the poll.
      thunkAPI.dispatch(artworkSlice.actions.setValidationStatusForFile(result));

      if (result.validation_status !== 'pending') {
        return result;
      }

      // Sleep `POLLING_INTERVAL_MS` between attempts. Use a setTimeout
      // promise — NOT setInterval — so each tick checks the signal
      // before scheduling the next poll. Resolves early on abort so
      // the loop unblocks immediately.
      await new Promise<void>((resolve) => {
        const timer = setTimeout(resolve, POLLING_INTERVAL_MS);
        const onAbort = (): void => {
          clearTimeout(timer);
          resolve();
        };
        if (thunkAPI.signal.aborted) {
          clearTimeout(timer);
          resolve();
        } else {
          thunkAPI.signal.addEventListener('abort', onAbort, { once: true });
        }
      });
    }

    // Exhausted attempts — surface a timeout error and let the
    // rejected handler keep the last-known status in place.
    throw new Error(
      `Validation polling timed out after ${POLLING_MAX_ATTEMPTS} attempts`,
    );
  },
);

/**
 * Argument shape for {@link attachFileToOrderItem}. Mirrors the wire
 * body — `is_primary` is optional because the platform defaults to
 * `false` when omitted.
 */
export interface AttachFileToOrderItemArg {
  orderId: number;
  artworkFileId: number;
  isPrimary?: boolean;
}

/**
 * `POST /api/v1/orders/:orderId/artwork` — attach an uploaded artwork
 * file to a specific order item. Returns the pivot row the platform
 * persisted (so callers can read the canonical `is_primary` value).
 */
export const attachFileToOrderItem = createAsyncThunk<
  AttachFileToOrderItemResponse,
  AttachFileToOrderItemArg
>('artwork/attachFileToOrderItem', async (arg) =>
  artworkApi.attachFileToOrderItem(arg.orderId, arg.artworkFileId, arg.isPrimary),
);

// ----------------------------------------------------------------------------
// Selected-artwork thunks (SH-ARTWORK-001)
// ----------------------------------------------------------------------------

/**
 * Argument shape for {@link selectArtworkById}. `thumbnailUrl` is
 * optional — when omitted, the thunk uses the file's server-minted
 * `preview_url` (presigned, short-lived). Callers should only supply
 * a URL when they have a more specific one (e.g. an already-decoded
 * blob URL).
 */
export interface SelectArtworkByIdArg {
  artworkFileId: number;
  /** Override the file's `preview_url`. Optional. */
  thumbnailUrl?: string;
  /** Auto-attach on first add-to-cart? Defaults to `true`. */
  autoAttach?: boolean;
}

/**
 * Resolves an `ArtworkFile` by id (cache hit on `state.files` first;
 * falls back to refetching the library when missing) and dispatches
 * `setSelectedArtwork` with the resulting projection.
 *
 * The library refetch is intentional — a single-file endpoint
 * (`GET /account/artwork/:id`) isn't part of the platform's public
 * artwork surface, and libraries are small (10–50 entries), so the
 * second-best fallback is to refresh the cache and pick the row out.
 * If the id isn't present after the refetch, the thunk rejects with
 * a "not found" error.
 */
export const selectArtworkById = createAsyncThunk<
  SelectedArtwork,
  SelectArtworkByIdArg
>('artwork/selectArtworkById', async (arg, thunkAPI) => {
  const state = thunkAPI.getState() as { artwork: ArtworkSliceState };
  let file = state.artwork.files.find((f) => f.id === arg.artworkFileId);

  if (file === undefined) {
    const fresh = await artworkApi.fetchArtworkLibrary();
    // Push the refreshed library into the slice so future selections
    // hit the cache.
    thunkAPI.dispatch(fetchArtworkLibrary.fulfilled(fresh, '', undefined));
    file = fresh.find((f) => f.id === arg.artworkFileId);
  }

  if (file === undefined) {
    throw new Error(`Artwork file ${arg.artworkFileId} not found in library`);
  }

  const thumbnailUrl = arg.thumbnailUrl ?? file.preview_url ?? null;
  if (thumbnailUrl === null) {
    throw new Error(
      `Artwork file ${arg.artworkFileId} has no preview_url and no override was supplied`,
    );
  }

  return {
    artworkFileId: file.id,
    thumbnailUrl,
    filename: file.original_filename,
    widthPx: file.width_px,
    heightPx: file.height_px,
    dpi: file.dpi,
    selectedAt: new Date().toISOString(),
    autoAttach: arg.autoAttach ?? true,
  };
});

/**
 * Argument shape for {@link selectAnonymousArtwork}. The optional
 * `widthPx` / `heightPx` / `dpi` let the caller pass pre-decoded raster
 * metadata if it has already inspected the file; otherwise the slice
 * stores nulls and downstream paneling checks defer.
 *
 * `draftId` is the IndexedDB key from `artworkDraftStore.putDraft` — when
 * supplied, the thunk skips its own thumbnail generation and uses the
 * passed `thumbnailDataUrl` instead (the caller already generated it).
 */
export interface SelectAnonymousArtworkArg {
  file: File;
  widthPx?: number | null;
  heightPx?: number | null;
  dpi?: number | null;
  autoAttach?: boolean;
  /** IndexedDB draft id — present when the caller has already persisted the draft. */
  draftId?: string | null;
  /** Pre-generated thumbnail from `generateArtworkThumbnail` — preferred over the slice's own canvas attempt. */
  thumbnailDataUrl?: string | null;
}

/**
 * Anonymous-visitor flow: store the artwork selection with `artworkFileId: null`.
 * No network call. When `draftId` + `thumbnailDataUrl` are supplied (the
 * deferred-draft path), uses them directly instead of re-generating a thumbnail.
 * The actual server-side upload happens at checkout after account creation.
 */
export const selectAnonymousArtwork = createAsyncThunk<
  SelectedArtwork,
  SelectAnonymousArtworkArg
>('artwork/selectAnonymousArtwork', async (arg) => {
  // Prefer the caller-supplied thumbnail (from artworkThumbnail.ts), then the
  // slice's own canvas attempt. Both yield portable data URLs and return null
  // for non-renderable formats (TIFF/PSD/AI/EPS/PDF). Leave it null in that
  // case — the preview capsule shows a placeholder icon. An object URL of the
  // raw file would render a broken <img> (the browser can't decode it) and a
  // blob: URL wouldn't survive a new document/tab anyway.
  const thumbnailUrl =
    arg.thumbnailDataUrl ?? (await generateThumbnail(arg.file)) ?? null;

  return {
    artworkFileId: null,
    thumbnailUrl,
    filename: arg.file.name,
    widthPx: arg.widthPx ?? null,
    heightPx: arg.heightPx ?? null,
    dpi: arg.dpi ?? null,
    selectedAt: new Date().toISOString(),
    autoAttach: arg.autoAttach ?? true,
    draftId: arg.draftId ?? null,
  };
});

/**
 * Promote an anonymous selection (no `artworkFileId`) into a persisted
 * one by uploading the pending `File` and dispatching `setSelectedArtwork`
 * with the server-confirmed id.
 *
 * No-ops when the pending file is absent or when the current selection
 * is already persisted — safe to fire on every `login.fulfilled`.
 *
 * Keeps the original blob thumbnail URL — it's still valid in this tab
 * for the lifetime of the session, so the preview doesn't flicker
 * during the upload. The next library refetch will surface the
 * server's `preview_url`.
 */
export const promoteAnonymousArtwork = createAsyncThunk<
  SelectedArtwork | null,
  void
>('artwork/promoteAnonymousArtwork', async (_arg, thunkAPI) => {
  const state = thunkAPI.getState() as { artwork: ArtworkSliceState };
  const current = state.artwork.selectedArtwork;
  if (current === null || current.artworkFileId !== null) {
    return null;
  }

  const file = readPendingArtworkFile();
  if (file === null) {
    return null;
  }

  try {
    const dto = await uploadArtworkFileApi({ file });
    clearPendingArtworkFile();
    return {
      ...current,
      artworkFileId: dto.id,
      filename: dto.original_filename,
    };
  } catch (error) {
    clearPendingArtworkFile();
    throw error;
  }
});

// ----------------------------------------------------------------------------
// Slice
// ----------------------------------------------------------------------------

const artworkSlice = createSlice({
  name: 'artwork',
  initialState,
  reducers: {
    /**
     * Upsert a single upload-queue entry from the FileUploader's
     * legacy {@link ArtworkUploadProgress} shape. Translates to the
     * canonical {@link UploadQueueEntry} before storing so the queue
     * has one consistent shape regardless of producer.
     */
    artworkUploadProgress(state, action: PayloadAction<ArtworkUploadProgress>) {
      const entry = progressToQueueEntry(action.payload);
      upsertQueueEntry(state.uploadQueue, entry);
    },
    /**
     * Remove a single upload entry — fired when the user dismisses a
     * file card (Cancel during upload, or X after a terminal state).
     */
    artworkUploadRemoved(state, action: PayloadAction<{ uploadId: string }>) {
      state.uploadQueue = state.uploadQueue.filter(
        (entry) => entry.id !== action.payload.uploadId,
      );
    },
    /** Clear the entire upload queue. */
    artworkUploadsCleared(state) {
      state.uploadQueue = [];
    },
    /**
     * Internal action dispatched by the polling thunk on every
     * successful poll tick. Updates the matching {@link ArtworkFile}
     * in `files` in place so the UI reflects the latest validation
     * status without waiting for the loop to fulfill.
     */
    setValidationStatusForFile(
      state,
      action: PayloadAction<
        Pick<ArtworkFile, 'id' | 'validation_status' | 'validation_results_json'>
      >,
    ) {
      const idx = state.files.findIndex(
        (file) => file.id === action.payload.id,
      );
      if (idx !== -1) {
        state.files[idx] = {
          ...state.files[idx],
          validation_status: action.payload.validation_status,
          validation_results_json: action.payload.validation_results_json,
        };
      }
    },

    // ------------------------------------------------------------------
    // Selected-artwork actions (SH-ARTWORK-001)
    // ------------------------------------------------------------------

    /**
     * Set the customer's "currently selected for next product" choice.
     * Revokes the previous selection's object URL (if any) so the blob
     * doesn't leak. Echoes to sessionStorage for same-tab survival.
     */
    setSelectedArtwork(state, action: PayloadAction<SelectedArtwork>) {
      // Revoke prior anonymous blob before replacing.
      if (
        state.selectedArtwork !== null &&
        state.selectedArtwork.artworkFileId === null
      ) {
        revokeIfObjectUrl(state.selectedArtwork.thumbnailUrl);
      }
      state.selectedArtwork = action.payload;
      writeSessionEcho(action.payload);
    },

    /**
     * Clear the current selection. Revokes any object URL and removes
     * the sessionStorage echo.
     */
    clearSelectedArtwork(state) {
      if (
        state.selectedArtwork !== null &&
        state.selectedArtwork.artworkFileId === null
      ) {
        revokeIfObjectUrl(state.selectedArtwork.thumbnailUrl);
      }
      state.selectedArtwork = null;
      writeSessionEcho(null);
    },
  },
  extraReducers: (builder) => {
    // ------------------------------------------------------------------
    // fetchArtworkLibrary
    // ------------------------------------------------------------------
    builder
      .addCase(fetchArtworkLibrary.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchArtworkLibrary.fulfilled, (state, action) => {
        state.isLoading = false;
        state.files = action.payload;
      })
      .addCase(fetchArtworkLibrary.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message ?? 'Unknown error';
      });

    // ------------------------------------------------------------------
    // deleteArtworkFile
    // ------------------------------------------------------------------
    builder
      .addCase(deleteArtworkFile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteArtworkFile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.files = state.files.filter((file) => file.id !== action.payload);
      })
      .addCase(deleteArtworkFile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message ?? 'Unknown error';
      });

    // ------------------------------------------------------------------
    // uploadArtworkFile — merges completed upload into files + queue
    // ------------------------------------------------------------------
    builder.addCase(uploadArtworkFile.fulfilled, (state, action) => {
      const { uploadId, file } = action.payload;

      // Merge the file into `files`, deduping by id so a re-dispatch
      // doesn't create a duplicate library record.
      const existingFileIdx = state.files.findIndex((f) => f.id === file.id);
      if (existingFileIdx === -1) {
        state.files.unshift(file);
      } else {
        state.files[existingFileIdx] = file;
      }

      // Mark the matching queue entry complete. If no entry exists yet
      // (the producer never dispatched a `queued` / `uploading` tick),
      // synthesize one so consumers always see the terminal state.
      upsertQueueEntry(state.uploadQueue, {
        id: uploadId,
        filename: file.original_filename,
        progress: 100,
        status: 'complete',
        artworkFileId: file.id,
      });
    });
    builder.addCase(uploadArtworkFile.rejected, (state, action) => {
      state.error = action.error.message ?? 'Unknown error';
    });

    // ------------------------------------------------------------------
    // fetchFileValidationStatus — polling
    // ------------------------------------------------------------------
    builder.addCase(fetchFileValidationStatus.fulfilled, (state, action) => {
      const idx = state.files.findIndex((file) => file.id === action.payload.id);
      if (idx !== -1) {
        state.files[idx] = {
          ...state.files[idx],
          validation_status: action.payload.validation_status,
          validation_results_json: action.payload.validation_results_json,
        };
      }
    });
    builder.addCase(fetchFileValidationStatus.rejected, (state, action) => {
      state.error = action.error.message ?? 'Unknown error';
    });

    // ------------------------------------------------------------------
    // attachFileToOrderItem
    // ------------------------------------------------------------------
    builder
      .addCase(attachFileToOrderItem.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(attachFileToOrderItem.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(attachFileToOrderItem.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message ?? 'Unknown error';
      });

    // ------------------------------------------------------------------
    // selectArtworkById (SH-ARTWORK-001)
    // ------------------------------------------------------------------
    builder
      .addCase(selectArtworkById.fulfilled, (state, action) => {
        if (
          state.selectedArtwork !== null &&
          state.selectedArtwork.artworkFileId === null
        ) {
          revokeIfObjectUrl(state.selectedArtwork.thumbnailUrl);
        }
        state.selectedArtwork = action.payload;
        writeSessionEcho(action.payload);
      })
      .addCase(selectArtworkById.rejected, (state, action) => {
        state.error = action.error.message ?? 'Unknown error';
      });

    // ------------------------------------------------------------------
    // selectAnonymousArtwork (SH-ARTWORK-001)
    // ------------------------------------------------------------------
    builder
      .addCase(selectAnonymousArtwork.fulfilled, (state, action) => {
        if (
          state.selectedArtwork !== null &&
          state.selectedArtwork.artworkFileId === null
        ) {
          revokeIfObjectUrl(state.selectedArtwork.thumbnailUrl);
        }
        state.selectedArtwork = action.payload;
        writeSessionEcho(action.payload);
      })
      .addCase(selectAnonymousArtwork.rejected, (state, action) => {
        state.error = action.error.message ?? 'Unknown error';
      });

    // ------------------------------------------------------------------
    // promoteAnonymousArtwork — swap an anonymous selection for the
    // persisted one minted at login.
    // ------------------------------------------------------------------
    builder
      .addCase(promoteAnonymousArtwork.fulfilled, (state, action) => {
        if (action.payload === null) return;
        state.selectedArtwork = action.payload;
        writeSessionEcho(action.payload);
      })
      .addCase(promoteAnonymousArtwork.rejected, (state, action) => {
        state.error = action.error.message ?? 'Unknown error';
      });

    // ------------------------------------------------------------------
    // auth/logout — clear selection so the next session starts clean
    // (SH-ARTWORK-001)
    // ------------------------------------------------------------------
    builder.addCase(logout.fulfilled, (state) => {
      if (
        state.selectedArtwork !== null &&
        state.selectedArtwork.artworkFileId === null
      ) {
        revokeIfObjectUrl(state.selectedArtwork.thumbnailUrl);
      }
      state.selectedArtwork = null;
      writeSessionEcho(null);
    });
  },
});

export const {
  artworkUploadProgress,
  artworkUploadRemoved,
  artworkUploadsCleared,
  setValidationStatusForFile,
  setSelectedArtwork,
  clearSelectedArtwork,
} = artworkSlice.actions;

export const artworkReducer = artworkSlice.reducer;

// ----------------------------------------------------------------------------
// Listener middleware — promote anonymous selection on login
// ----------------------------------------------------------------------------

export const artworkListenerMiddleware = createListenerMiddleware();

artworkListenerMiddleware.startListening({
  actionCreator: login.fulfilled,
  effect: async (_action, listenerApi) => {
    await listenerApi.dispatch(promoteAnonymousArtwork());
  },
});

// ----------------------------------------------------------------------------
// Selectors
// ----------------------------------------------------------------------------

interface RootSliceState {
  artwork: ArtworkSliceState;
}

const selectArtworkState = (state: RootSliceState): ArtworkSliceState =>
  state.artwork;

/**
 * Selects the persisted artwork library array. Memoised so subscribed
 * components don't re-render unless `files` itself changes.
 */
export const selectArtworkLibrary = createSelector(
  [selectArtworkState],
  (artwork): ArtworkFile[] => artwork.files,
);

/**
 * Selects the transient upload queue. Memoised so subscribed
 * components don't re-render on unrelated slice updates.
 */
export const selectUploadQueue = createSelector(
  [selectArtworkState],
  (artwork): UploadQueueEntry[] => artwork.uploadQueue,
);

/**
 * Factory selector — returns a memoised selector keyed by `id` so the
 * same id reuses the same selector instance. Accepts numeric file ids.
 *
 * Returns `undefined` when no record matches.
 */
export const selectFileById = (
  id: number,
): ((state: RootSliceState) => ArtworkFile | undefined) =>
  createSelector(
    [selectArtworkLibrary],
    (files) => files.find((file) => file.id === id),
  );

/**
 * Factory selector — returns a memoised selector that resolves the
 * validation status for a given file id. Returns `undefined` when no
 * record matches.
 */
export const selectValidationStatusById = (
  id: number,
): ((state: RootSliceState) => ValidationStatus | undefined) =>
  createSelector(
    [selectArtworkLibrary],
    (files) => files.find((file) => file.id === id)?.validation_status,
  );

// ----------------------------------------------------------------------------
// Legacy selectors — kept for SH-FILES-001 / FileUploader compatibility.
// They project the new `uploadQueue` array shape back onto the original
// stub's `ArtworkUploadProgress` projection so consumers compile
// unchanged.
// ----------------------------------------------------------------------------

function queueEntryToProgress(entry: UploadQueueEntry): ArtworkUploadProgress {
  const progress: ArtworkUploadProgress = {
    uploadId: entry.id,
    filename: entry.filename,
    percent: entry.progress,
    status: entry.status,
  };
  if (entry.error !== undefined) {
    progress.errorMessage = entry.error;
  }
  return progress;
}

export const selectArtworkUploadById =
  (uploadId: string) =>
  (state: RootSliceState): ArtworkUploadProgress | undefined => {
    const entry = state.artwork.uploadQueue.find((e) => e.id === uploadId);
    return entry ? queueEntryToProgress(entry) : undefined;
  };

export const selectAllArtworkUploads = (
  state: RootSliceState,
): ArtworkUploadProgress[] =>
  state.artwork.uploadQueue.map(queueEntryToProgress);

export const selectInFlightArtworkUploads = (
  state: RootSliceState,
): ArtworkUploadProgress[] =>
  state.artwork.uploadQueue
    .filter((entry) => entry.status === 'uploading' || entry.status === 'queued')
    .map(queueEntryToProgress);

// ----------------------------------------------------------------------------
// Selected-artwork selectors (SH-ARTWORK-001)
// ----------------------------------------------------------------------------

/**
 * The customer's currently-selected artwork, or `null` when no
 * selection is active.
 */
export const selectSelectedArtwork = createSelector(
  [selectArtworkState],
  (artwork): SelectedArtwork | null => artwork.selectedArtwork,
);

/**
 * Boolean shortcut for layout decisions (e.g. the configurator's
 * upload zone hides itself when a selection is present).
 */
export const selectHasSelectedArtwork = createSelector(
  [selectArtworkState],
  (artwork): boolean => artwork.selectedArtwork !== null,
);

/**
 * Boolean shortcut for "the selected artwork has a server id" — i.e.
 * the customer is signed in and the artwork already lives in their
 * library. Useful for cart-add flow decisions (anonymous selections
 * need an upload-then-attach; persisted ones attach directly).
 */
export const selectIsSelectedArtworkPersisted = createSelector(
  [selectArtworkState],
  (artwork): boolean =>
    artwork.selectedArtwork !== null &&
    artwork.selectedArtwork.artworkFileId !== null,
);
